import { createElement, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import type { GridColDef, GridFilterItem, GridSortCellParams } from "@mui/x-data-grid";
import { ColumnFilterButton, type AntdFilterOption, type FilterValue } from "./ColumnFilterButton";

// Antd-Table-column -> MUI DataGrid column mapping. Types are widened to
// structurally accept antd's ColumnType (ReactNode title, dataIndex string|
// string[], sorter object forms, extra fields via index signature).

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface AntdColumn<T = any> {
  title?: ReactNode;
  dataIndex?: string | number | Array<string | number>;
  key?: string | number;
  render?: (value: any, record: T, index: number) => ReactNode;
  sorter?: boolean | ((a: T, b: T) => number) | { compare?: (a: T, b: T) => number; multiple?: number };
  defaultSortOrder?: "ascend" | "descend" | null;
  width?: number | string;
  align?: "left" | "center" | "right";
  fixed?: "left" | "right" | boolean;
  ellipsis?: boolean;
  [k: string]: any;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// antd's ColumnsType alias (call sites type their column arrays with this).
export type ColumnsType<T = unknown> = AntdColumn<T>[];
export type ColumnType<T = unknown> = AntdColumn<T>;

export const columnField = <T,>(c: AntdColumn<T>, index: number): string => {
  const di = Array.isArray(c.dataIndex) ? c.dataIndex.join(".") : c.dataIndex;
  return di != null ? String(di) : c.key != null ? String(c.key) : `col_${index}`;
};

// P3 left-pin shim (opt-in). Community DataGrid has NO column-pinning API (Pro only),
// but its CSS already ships `.cell--pinnedLeft`/`.columnHeader--pinnedLeft` sticky
// rules. We tag the first N columns by identity (cell/headerClassName) so DataTable can
// apply `position: sticky` + a cumulative `left` offset. Class-based targeting is immune
// to the colSpan group rows that would break nth-child targeting.
export const pinnedLeftClass = (i: number): string => `rcg-pinned-left-${i}`;
// P3 right-pin shim (opt-in), mirror of pinnedLeftClass. Tags the LAST m columns by
// absolute index; DataTable applies `position: sticky` + a cumulative `right` offset.
export const pinnedRightClass = (i: number): string => `rcg-pinned-right-${i}`;

// antd cell/header alignment -> flex justification.
const alignToJustify = (align?: "left" | "center" | "right"): "flex-start" | "center" | "flex-end" =>
  align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start";

// Full-bleed (absolute inset:0) layout so an onCell/onHeaderCell backgroundColor fills the
// whole padded DataGrid cell/header — a continuous band, not a floating box inset by padding.
const fullBleedStyle = (align?: "left" | "center" | "right", extra?: Record<string, unknown>) => ({
  position: "absolute" as const,
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: alignToJustify(align),
  // Horizontal inset MUST equal the .MuiDataGrid-cell padding (DataTable sets 4px) so a
  // full-bleed (absolute inset:0) cell — which escapes the cell's own padding — lands its
  // content on the SAME edge as a normal-flow cell (which sits inside that padding). If
  // these two diverge, right-aligned summary/subtotal values (full-bleed, they have a bg)
  // stop sharing a right edge with normal line-item values (normal-flow). Keep in sync.
  padding: "0 4px",
  boxSizing: "border-box" as const,
  ...extra,
});

// antd column `ellipsis`: truncate long text with an ellipsis instead of wrapping /
// widening the cell. MUI only clips non-rendered cells; render/onCell cells need this.
const ELLIPSIS_STYLE = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 } as const;

// antd's `sorter` is either a comparator or `true` (sort by the raw cell value, which is
// MUI's own default) or an object wrapping one. Return a comparator only for the forms
// that carry custom logic; `true` needs no mapping.
export const antdComparator = <T,>(sorter: AntdColumn<T>["sorter"]): ((a: T, b: T) => number) | undefined => {
  if (typeof sorter === "function") return sorter;
  if (sorter && typeof sorter === "object" && typeof sorter.compare === "function") return sorter.compare;
  return undefined;
};

// antd's column filters (`filters` + `onFilter`) have no MUI equivalent: MUI filters by
// operator against a cell value, antd by an arbitrary predicate over the whole row. Map
// the antd shape onto ONE custom operator whose value is the array of ticked options —
// a row survives if ANY of them passes onFilter, which is antd's OR semantics.
export const ANTD_FILTER_OPERATOR = "antdIn";

/** One column's rule: a row survives if ANY ticked option passes its onFilter. */
const matchesColumn = <T,>(
  onFilter: (value: FilterValue, record: T) => boolean,
  selected: FilterValue[],
  row: T,
): boolean => selected.some((v) => onFilter(v, row));

const antdFilterOperator = <T,>(onFilter: (value: FilterValue, record: T) => boolean) => ({
  value: ANTD_FILTER_OPERATOR,
  // MUI resolves an operator's display name as `label || getLocaleText("filterOperator" +
  // capitalize(value))` and THROWS when neither exists. A custom operator has no locale
  // entry, so the label is mandatory, not decoration.
  label: "is any of",
  getApplyFilterFn: (item: GridFilterItem) => {
    const selected = (item.value ?? []) as FilterValue[];
    // null = "skip this item", which is how MUI expresses an inactive filter.
    if (!selected.length) return null;
    return (_cellValue: unknown, row: T) => matchesColumn(onFilter, selected, row);
  },
  requiresFilterValue: true,
});

/**
 * The rows the grid is actually showing, for a consumer that has to reproduce
 * the visible set OUTSIDE the grid (the summary footer, which antd handed the
 * current page's data). Applies the same rule the grid does: each column ORs its
 * own ticked options via `matchesColumn`, and the columns AND together, which is
 * MUI's default logicOperator.
 */
export const visibleRowsForFilters = <T,>(
  columns: AntdColumn<T>[],
  selectionsByField: Record<string, FilterValue[]>,
  rows: T[],
): T[] => {
  const active = Object.entries(selectionsByField).filter(([, selected]) => selected.length);
  if (!active.length) return rows;

  const onFilterFor = (field: string) =>
    columns.find((c, i) => columnField(c, i) === field)?.onFilter as
      | ((value: FilterValue, record: T) => boolean)
      | undefined;

  return rows.filter((row) =>
    active.every(([field, selected]) => {
      const onFilter = onFilterFor(field);
      // A filtered column with no onFilter cannot narrow anything (the grid's
      // operator returns null for it too), so it keeps every row.
      return !onFilter || matchesColumn(onFilter, selected, row);
    }),
  );
};

/** Lets DataTable own the filter selection while the adapter renders the control. */
export interface FilterControl {
  selectedFor: (field: string) => FilterValue[];
  onChangeFor: (field: string) => (next: FilterValue[]) => void;
}

export const toGridColumns = <T,>(columns: AntdColumn<T>[], pinnedLeftColumns = 0, pinnedRightColumns = 0, fillWidth = false, filterControl?: FilterControl): GridColDef[] =>
  columns.map((c, i) => {
    const field = columnField(c, i);
    const titleIsString = typeof c.title === "string";
    const col: GridColDef = {
      field,
      headerName: titleIsString ? (c.title as string) : "",
      sortable: Boolean(c.sorter),
      // antd compared whole ROWS (`sorter(a, b)`); MUI sorts by the cell value at
      // `row[field]` unless given a comparator. Any column whose data is not literally
      // at `row[field]` — a nested dataIndex ("investment.vintageYear"), or a key naming
      // something the row reaches through ("relativeDPIQuartile") — therefore sorted by
      // `undefined`, i.e. not at all, and every custom tie/null rule was ignored.
      // Hand antd's comparator back to MUI, resolving each side to its row.
      ...(antdComparator(c.sorter)
        ? {
            sortComparator: ((_v1: unknown, _v2: unknown, p1: GridSortCellParams, p2: GridSortCellParams) =>
              antdComparator(c.sorter)!(
                p1.api.getRow(p1.id) as T,
                p2.api.getRow(p2.id) as T,
              )) as GridColDef["sortComparator"],
          }
        : {}),
      ...(c.align ? { align: c.align, headerAlign: c.align } : {}),
      // minWidth defaults to 50 in DataGrid and clamps `width` UP — so a thin
      // divider/spacer column (width:2) would render ~50px. Pin minWidth to the
      // requested width so small columns stay small.
      //
      // fillWidth restores antd's sizing: antd spread a table's LEFTOVER width
      // across its columns, so a declared width behaved as a ratio with a floor.
      // MUI leaves that slack as dead space right of the last column, so map the
      // width to `flex` (the ratio) + `minWidth` (the floor) instead.
      ...(c.width && typeof c.width === "number"
        ? fillWidth
          ? { flex: c.width, minWidth: c.width }
          : { width: c.width, minWidth: c.width }
        : { flex: 1 }),
    };
    // P3: tag the first N columns for the left-pin shim (see pinnedLeftClass). The
    // sticky positioning + cumulative `left` offset are applied in DataTable's sx.
    if (i < pinnedLeftColumns) {
      col.cellClassName = pinnedLeftClass(i);
      col.headerClassName = pinnedLeftClass(i);
    } else if (pinnedRightColumns && i >= columns.length - pinnedRightColumns) {
      col.cellClassName = pinnedRightClass(i);
      col.headerClassName = pinnedRightClass(i);
    }
    // antd column filters -> a real MUI filter operator + a header affordance. Only
    // wired when the table opted in by passing a filterControl; otherwise the antd
    // props stay inert, exactly as before.
    const antdFilters = c.filters as AntdFilterOption[] | undefined;
    const onFilter = c.onFilter as ((value: FilterValue, record: T) => boolean) | undefined;
    const filterable = Boolean(filterControl && antdFilters?.length && onFilter);
    if (filterable && filterControl && onFilter) {
      col.filterable = true;
      col.filterOperators = [antdFilterOperator<T>(onFilter)];
    } else {
      // MUI defaults every column to filterable; without an affordance that is a lie.
      col.filterable = false;
    }
    if (!titleIsString && c.title != null) {
      col.renderHeader = () => c.title as ReactNode;
    }
    if (filterable && filterControl) {
      const headerContent: ReactNode = c.title ?? col.headerName ?? null;
      col.renderHeader = () =>
        createElement(
          "span",
          { style: { display: "inline-flex", alignItems: "center", gap: 2, minWidth: 0 } },
          createElement("span", { style: ELLIPSIS_STYLE, key: "t" }, headerContent),
          createElement(ColumnFilterButton, {
            key: "f",
            options: antdFilters ?? [],
            selected: filterControl.selectedFor(field),
            onChange: filterControl.onChangeFor(field),
            label: typeof c.title === "string" ? c.title : field,
          }),
        );
    }
    // antd onHeaderCell(col) returns per-header-cell DOM props (style/className) —
    // the asset-allocation grid uses it to color group headers (MTD=gold,
    // Redemption=green, Commitment=blue). MUI DataGrid has no per-header attr API,
    // so render a full-bleed background layer (absolute inset:0 fills the padded
    // header cell, matching antd's <th> background rather than a floating box).
    const onHeaderCell = c.onHeaderCell as (() => { style?: Record<string, unknown>; className?: string }) | undefined;
    if (onHeaderCell) {
      const headerProps = onHeaderCell() || {};
      const headerContent: ReactNode = c.title ?? col.headerName ?? null;
      col.renderHeader = () =>
        createElement(
          "div",
          { className: headerProps.className, style: fullBleedStyle(c.align, headerProps.style as Record<string, unknown>) },
          headerContent,
        );
    }
    // antd onCell(record,index) returns per-cell DOM props (data-cy, title, style,
    // onClick). MUI DataGrid has no per-cell attr API, so wrap the rendered content
    // in a div carrying them ([data-cy=...] then resolves inside the cell).
    const onCell = c.onCell as ((record: T, index: number) => Record<string, unknown>) | undefined;
    // antd merges cells via onCell().colSpan (e.g. the allocation grid spans a group
    // row's name across its tag columns, with colSpan:0 on the covered cells). MUI
    // DataGrid has native colSpan, so map it: the spanning cell returns its span; a
    // covered cell (antd colSpan 0) maps to 1 — MUI already skips it because the
    // preceding spanning cell covers it. Columns without colSpan are unaffected.
    if (onCell) {
      col.colSpan = ((_v: unknown, row: unknown) => {
        const cs = (onCell(row as T, 0) || {}).colSpan;
        return typeof cs === "number" && cs > 1 ? cs : 1;
      }) as GridColDef["colSpan"];
    }
    if (c.render || onCell) {
      const render = c.render;
      col.renderCell = (params) => {
        const idx = params.api?.getRowIndexRelativeToVisibleRows?.(params.id) ?? 0;
        const content = (render ? render(params.value, params.row as T, idx) : (params.value as ReactNode)) ?? null;
        // render-only (no onCell) cells return bare content; wrap ONLY ellipsis columns so
        // they truncate (non-ellipsis columns keep their existing bare-content layout).
        if (!onCell) {
          return c.ellipsis
            ? createElement("div", { style: { width: "100%", textAlign: c.align, ...ELLIPSIS_STYLE } }, content)
            : (content as ReactNode);
        }
        const cellProps = onCell(params.row as T, idx) || {};
        // MUI DataGrid's cell handlers steal focus (on mousedown) and hijack keys
        // (arrows/Enter) for grid navigation. For interactive cell content (e.g. the
        // AssetAllocation inline-edit <input>), that stolen focus fires a premature
        // blur that discards the edit before the typed value is saved. Stop mousedown
        // + keydown from reaching the grid so the cell's own input keeps focus/keys.
        // Click still bubbles, so row-click selection is unaffected.
        const userMouseDown = cellProps.onMouseDown as ((e: ReactMouseEvent) => void) | undefined;
        const userKeyDown = cellProps.onKeyDown as ((e: ReactKeyboardEvent) => void) | undefined;
        const cellStyle = cellProps.style as Record<string, unknown> | undefined;
        // When onCell sets a backgroundColor (the allocation group/subtotal band, divider
        // columns), fill the whole padded DataGrid cell (absolute inset:0) so the color is
        // a CONTINUOUS band instead of a floating box inset by the cell padding. Interactive
        // cells without a background keep normal flow so inline-edit inputs are unaffected.
        const hasBg = Boolean(cellStyle && cellStyle.backgroundColor);
        return createElement(
          "div",
          {
            ...cellProps,
            onMouseDown: (e: ReactMouseEvent) => { userMouseDown?.(e); e.stopPropagation(); },
            onKeyDown: (e: ReactKeyboardEvent) => { userKeyDown?.(e); e.stopPropagation(); },
            // A rendered/onCell cell is wrapped in a width:100% div, which fills the
            // flex cell so MUI's `align` justification has nothing to push — the text
            // would fall back to left. Carry the column align through as textAlign so a
            // right-aligned column right-aligns by DEFINITION (normal-flow line items,
            // editable cells, and full-bleed summary rows all share one right edge).
            style: hasBg
              ? fullBleedStyle(c.align, cellStyle)
              : {
                  width: "100%",
                  textAlign: c.align,
                  ...(c.ellipsis ? ELLIPSIS_STYLE : {}),
                  ...(cellStyle as object),
                },
          },
          content,
        );
      };
    }
    return col;
  });

// antd grouped columns nest `children`; MUI needs flat leaves (UNIQUE field) + a
// columnGroupingModel. Leaf keys often repeat across sibling groups (e.g. each
// period group has additions/redemptions), which would produce duplicate DataGrid
// fields and CRASH the grid — so grouped leaves get a group-prefixed field. Fields
// are identity-only here (these columns render via render/onCell), so this is safe.
export const flattenColumnGroups = <T = unknown,>(
  cols: AntdColumn<T>[],
): { columns: AntdColumn<T>[]; grouping: ColumnGrouping[] } => {
  const flat: AntdColumn<T>[] = [];
  const grouping: ColumnGrouping[] = [];
  const walk = (list: AntdColumn<T>[], groupKey: string | null) => {
    list.forEach((c, i) => {
      if (Array.isArray(c.children) && c.children.length) {
        const gid = `grp_${grouping.length}`;
        const start = flat.length;
        walk(c.children as AntdColumn<T>[], gid);
        grouping.push({
          groupId: gid,
          headerName: typeof c.title === "string" ? c.title : String(c.title ?? gid),
          // antd centered a group header over its children; MUI defaults left, which
          // looks wrong over right-aligned number columns. Center to match.
          headerAlign: "center",
          children: flat.slice(start).map((lc, j) => ({ field: columnField(lc, start + j) })),
        });
      } else if (groupKey) {
        // group-prefix the key so sibling groups reusing the same key stay unique
        flat.push({ ...c, key: `${groupKey}__${c.key ?? c.dataIndex ?? i}` });
      } else {
        flat.push(c);
      }
    });
  };
  walk(cols, null);
  return { columns: flat, grouping };
};

// Extract antd <Column>/<ColumnGroup> children into a columns array + a
// DataGrid columnGroupingModel (for grouped headers). Used when call sites use
// the declarative Column API instead of the `columns` prop.
export interface ColumnGrouping {
  groupId: string;
  headerName: string;
  headerAlign?: "left" | "center" | "right";
  children: Array<{ field: string }>;
}
export const childrenToColumns = (
  children: unknown,
): { columns: AntdColumn[]; grouping: ColumnGrouping[] } => {
  const columns: AntdColumn[] = [];
  const grouping: ColumnGrouping[] = [];
  const reactChildren = (Array.isArray(children) ? children : [children]).filter(Boolean) as Array<{
    props?: Record<string, unknown>;
    key?: string | number | null;
  }>;
  for (const child of reactChildren) {
    const props = child?.props ?? {};
    if (Array.isArray(props.children) || (props.children && typeof props.children === "object" && "props" in (props.children as object))) {
      // ColumnGroup: has a title + child Columns
      const inner = childrenToColumns(props.children);
      if (props.title != null) {
        grouping.push({
          groupId: String(props.title),
          headerName: String(props.title),
          headerAlign: "center",
          children: inner.columns.map((c, i) => ({ field: columnField(c, columns.length + i) })),
        });
      }
      columns.push(...inner.columns);
    } else {
      columns.push(props as AntdColumn);
    }
  }
  return { columns, grouping };
};

export const resolveRowId = <T,>(
  rowKey: string | ((r: T) => string | number),
): ((r: T) => string | number) =>
  typeof rowKey === "function"
    ? rowKey
    : (r: T) => (r as Record<string, unknown>)[rowKey] as string | number;
